import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AzaanceConnectDialog, AZAANCE_LOGO } from "@/components/wallet/AzaanceConnectDialog";
import { isWalletConnectEnabled } from "@/lib/walletconnect";
import { toast } from "sonner";
import { Wallet, ChevronDown, Copy, LogOut, Mail, Blocks, Coins, Smartphone } from "lucide-react";
import { useWallet, isSocialLoginEnabled } from "@/contexts/WalletContext";
import { NAV } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { PortfolioSummary } from "@/components/wallet/Portfolio";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const ITEM = "text-zinc-300 hover:text-white focus:text-white cursor-pointer";
const truncateAddress = (addr) => (addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "");

const ConnectedMenu = () => {
  const navigate = useNavigate();
  const { address, disconnect } = useWallet();
  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    toast.success("Address copied");
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="border-[#D4AF37]/30 bg-[#D4AF37]/5 text-white hover:bg-[#D4AF37]/10 hover:text-white gap-2">
          <Wallet size={14} className="text-[#D4AF37]" />
          <span data-testid={NAV.walletAddress} className="font-mono text-sm">{truncateAddress(address)}</span>
          <ChevronDown size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-[#0A0A0A] border-[#2A2411]">
        <PortfolioSummary />
        <DropdownMenuItem onClick={copyAddress} className={ITEM}><Copy size={14} className="mr-2" /> Copy Address</DropdownMenuItem>
        <DropdownMenuItem data-testid="nav-my-tokens-link" onClick={() => navigate("/my-tokens")} className={ITEM}>
          <Coins size={14} className="mr-2" /> My Tokens
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/address/${address}`)} className={ITEM}>
          <Blocks size={14} className="mr-2" /> View on Explorer
        </DropdownMenuItem>
        <DropdownMenuItem onClick={disconnect} data-testid={NAV.disconnectBtn} className="text-red-400 hover:text-red-300 focus:text-red-300 cursor-pointer">
          <LogOut size={14} className="mr-2" /> Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Opens this page inside the wallet app's built-in browser, where the wallet is injected (no WalletConnect needed)
const openInApp = (app) => {
  const { host, pathname, search, href } = window.location;
  window.location.href = app === "metamask"
    ? `https://metamask.app.link/dapp/${host}${pathname}${search}`
    : `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(href)}`;
};

const MobileAppLinks = () => (
  <>
    <DropdownMenuSeparator className="bg-[#2A2411]" />
    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500 font-normal">On your phone</DropdownMenuLabel>
    <DropdownMenuItem data-testid="open-in-metamask-btn" onClick={() => openInApp("metamask")} className="cursor-pointer">
      <Smartphone size={14} className="mr-2 text-[#D4AF37]" /> Open in MetaMask app
    </DropdownMenuItem>
    <DropdownMenuItem data-testid="open-in-trust-btn" onClick={() => openInApp("trust")} className="cursor-pointer">
      <Smartphone size={14} className="mr-2 text-[#D4AF37]" /> Open in Trust Wallet app
    </DropdownMenuItem>
  </>
);

const ConnectMenu = () => {
  const { connect, connectSocial, isConnecting } = useWallet();
  const [azaanceOpen, setAzaanceOpen] = useState(false);
  const injectedLabel = window.ethereum?.isAzaance ? "Azaance (this browser)" : "Browser Wallet (MetaMask)";
  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid={NAV.connectWalletBtn} disabled={isConnecting}
          className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold rounded-full px-5 transition-transform active:scale-95">
          <Wallet size={14} className="mr-1.5" />
          {isConnecting ? "Connecting..." : "Connect"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-[#2A2411] text-white w-56">
        <DropdownMenuItem data-testid="connect-injected-btn" onClick={connect} className="cursor-pointer">
          <Wallet size={14} className="mr-2 text-[#D4AF37]" /> {injectedLabel}
        </DropdownMenuItem>
        {isWalletConnectEnabled && (
          <DropdownMenuItem data-testid="connect-azaance-btn" onClick={() => setAzaanceOpen(true)} className="cursor-pointer">
            <img src={AZAANCE_LOGO} alt="" className="w-3.5 h-3.5 rounded-sm mr-2" /> Azaance Wallet
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="connect-social-btn" onClick={connectSocial} disabled={!isSocialLoginEnabled} className="cursor-pointer">
          <Mail size={14} className="mr-2 text-[#D4AF37]" />
          {isSocialLoginEnabled ? "Google, email & more" : "Social login (not configured)"}
        </DropdownMenuItem>
        {!window.ethereum && <MobileAppLinks />}
      </DropdownMenuContent>
    </DropdownMenu>
    <AzaanceConnectDialog open={azaanceOpen} onOpenChange={setAzaanceOpen} />
    </>
  );
};

export const WalletMenu = () => {
  const { address } = useWallet();
  return address ? <ConnectedMenu /> : <ConnectMenu />;
};
